import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import { CheckCircleFill, XCircleFill } from 'react-bootstrap-icons';
import { useAuth } from '../contexts/AuthContext';
import { getStoredAuthUser } from '../utils/authStorage';
import { userHasOrganization } from './setPasswordFlow';
import './auth-shared.css';

/* ===========================================================
   Verify Email Page Component
   Automatically verifies the user's email when they click the
   link in their verification email. Extracts the token from
   the URL query parameter and calls the backend API.
   =========================================================== */

/* ---------- Main component ---------- */

const VerifyEmailNew: React.FC = () =>
{
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { verifyEmail } = useAuth();

    /* Extract the token from the URL */
    const token = searchParams.get('token') || '';

    /* -- Verification state -- */
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [errorMessage, setErrorMessage] = useState<string>('');

    /* Prevent double-invocation in React strict mode */
    const calledRef = useRef(false);

    /* ---------- Auto-verify on mount ---------- */

    useEffect(() =>
    {
        if (calledRef.current)
        {
            return;
        }
        calledRef.current = true;

        async function verify(): Promise<void>
        {
            if (!token)
            {
                setStatus('error');
                setErrorMessage('No verification token found in the link.');
                return;
            }

            try
            {
                const result = await verifyEmail(token);
                setStatus('success');

                /* Redirect after a short delay so the user sees the success message */
                setTimeout(() =>
                {
                    if (result?.mfa_required)
                    {
                        navigate('/mfa');
                    }
                    else if (result?.must_change_password)
                    {
                        navigate('/set-password');
                    }
                    else if (result?.is_superadmin)
                    {
                        navigate('/dashboard');
                    }
                    else
                    {
                                /* Self-registered user: already chose their own password,
                                    go to dashboard or organization setup */
                        let hasOrg = false;
                        try
                        {
                            hasOrg = userHasOrganization(getStoredAuthUser());
                        }
                        catch
                        {
                            hasOrg = false;
                        }
                        navigate(hasOrg ? '/dashboard' : '/profile-setup');
                    }
                }, 2000);
            }
            catch (err: any)
            {
                setStatus('error');
                const detail =
                    err.response?.data?.detail ||
                    'Verification failed. The link may be invalid or expired.';
                setErrorMessage(detail);
            }
        }

        verify();
    }, [token, verifyEmail, navigate]);

    /* ---------- Event handlers ---------- */

    /**
     * Navigate to login page when verification fails.
     */
    function handleGoToLogin(): void
    {
        navigate('/login');
    }

    /**
     * Navigate to resend verification page.
     */
    function handleResendVerification(): void
    {
        navigate('/register');
    }

    /**
     * Handles click on the backdrop (outside the card) to navigate to home.
     */
    function handleBackdropClick(e: React.MouseEvent<HTMLDivElement>): void
    {
        if (e.target === e.currentTarget)
        {
            navigate('/');
        }
    }

    /* ---------- Render helpers ---------- */

    /**
     * Renders the loading state while verification is in progress.
     */
    function renderLoading(): React.ReactElement
    {
        return (
            <>
                <Spinner animation="border" variant="primary" className="verify-spinner" />
                <h2 className="auth-heading verify-heading-gap">Verifying your email...</h2>
                <p className="verify-subtitle">Please wait while we confirm your account.</p>
            </>
        );
    }

    /**
     * Renders the success state after verification completes.
     */
    function renderSuccess(): React.ReactElement
    {
        return (
            <>
                <div className="verify-icon-wrapper verify-icon-success">
                    <CheckCircleFill size={40} />
                </div>
                <h2 className="auth-heading">Email Verified!</h2>
                <p className="verify-subtitle">
                    Your account has been activated successfully.
                </p>
                <p className="verify-subtitle">Redirecting you now...</p>
            </>
        );
    }

    /**
     * Renders the error state when verification fails.
     */
    function renderError(): React.ReactElement
    {
        return (
            <>
                <div className="verify-icon-wrapper verify-icon-error">
                    <XCircleFill size={40} />
                </div>
                <h2 className="auth-heading">Verification Failed</h2>
                <p className="verify-subtitle">{errorMessage}</p>

                <button
                    type="button"
                    className="auth-submit-btn verify-btn-gap"
                    onClick={handleGoToLogin}
                >
                    Go to Login
                </button>

                <div className="text-center">
                    <button
                        type="button"
                        className="verify-resend-link"
                        onClick={handleResendVerification}
                    >
                        Register again
                    </button>
                </div>
            </>
        );
    }

    /* ---------- Main render ---------- */

    return (
        <div className="auth-page" onClick={handleBackdropClick}>
            <div className="auth-card verify-card">
                <div className="text-center">
                    {status === 'loading' && renderLoading()}
                    {status === 'success' && renderSuccess()}
                    {status === 'error' && renderError()}
                </div>
            </div>
        </div>
    );
};

export default VerifyEmailNew;
